import { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import { TabItem } from '../types';
import { Tab } from './Tab';
import { useTabs } from './useTabs';

export type TabsProps = {
  items: TabItem[];
  onSelectTab?: (tab: TabItem) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  title?: string;
  disabled?: boolean;
  currentTab?: string;
  classNames?: {
    wrapper?: string;
    nav?: string;
    tabWrapper?: string;
  };
};

export const Tabs: FC<TabsProps> = ({ items, onSelectTab, onKeyDown, title, disabled, currentTab, classNames }) => {
  const { tabListId, handleKeyDown, openTab, selectTab } = useTabs({
    items,
    onSelectTab,
    onKeyDown,
    currentTab,
    disabled,
  });

  const selectedItem = items[openTab];

  return (
    <section className={twMerge('flex h-full w-full flex-col gap-6', classNames?.wrapper)}>
      {title && (
        <h2 id={`tablist-${tabListId}`} className='sr-only'>
          {title}
        </h2>
      )}

      <nav
        role='tablist'
        aria-labelledby={title ? `tablist-${tabListId}` : undefined}
        className={twMerge('flex w-full max-w-full flex-wrap gap-3 border-b border-secondary-border', classNames?.nav)}
      >
        {items.map((item, index) => (
          <Tab
            key={item.title}
            index={index}
            tabListId={tabListId}
            title={item.title}
            onClick={() => selectTab(index)}
            onKeyDown={handleKeyDown}
            isSelected={index === openTab}
            disabled={disabled}
            prefix={item.prefix}
          />
        ))}
      </nav>

      {selectedItem && (
        <div
          aria-labelledby={`tab-${tabListId}-${openTab}`}
          role='tabpanel'
          id={`tabpanel-${openTab}-${tabListId}`}
          className={twMerge('flex h-full flex-col', classNames?.tabWrapper)}
        >
          {selectedItem.element}
        </div>
      )}
    </section>
  );
};
